import { type ReactNode, type ButtonHTMLAttributes, type AnchorHTMLAttributes } from 'react';
import { Link, type LinkProps } from 'react-router';
import { clsx } from 'clsx';

type Variant = 'primary' | 'secondary' | 'dark';
type Size = 'sm' | 'md' | 'lg';

const BASE =
  'inline-flex items-center justify-center gap-2 border-2 border-charcoal-800 font-marketing-display font-bold whitespace-nowrap transition-transform duration-150 ease-out cursor-pointer select-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-lime-500 focus-visible:ring-offset-2 focus-visible:ring-offset-paper';

const VARIANTS: Record<Variant, string> = {
  primary:
    'bg-lime-500 text-charcoal-800 shadow-neobrutal-4 hover:-translate-x-[2px] hover:-translate-y-[2px] hover:shadow-neobrutal-6',
  secondary:
    'bg-white text-charcoal-800 shadow-neobrutal-4 hover:-translate-x-[2px] hover:-translate-y-[2px] hover:shadow-neobrutal-6',
  dark: 'bg-charcoal-800 text-white shadow-neobrutal-4 hover:-translate-x-[2px] hover:-translate-y-[2px] hover:shadow-neobrutal-6',
};

const SIZES: Record<Size, string> = {
  sm: 'px-4 py-2 text-sm',
  md: 'px-6 py-3 text-[15px]',
  lg: 'px-8 py-4 text-base',
};

type CommonProps = {
  variant?: Variant;
  size?: Size;
  className?: string;
  children: ReactNode;
};

type ButtonProps = CommonProps &
  Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'className' | 'children'> & {
    as?: 'button';
  };

type AnchorProps = CommonProps &
  Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'className' | 'children'> & {
    as: 'a';
    href: string;
  };

type RouterLinkProps = CommonProps &
  Omit<LinkProps, 'className' | 'children'> & {
    as: 'link';
    to: LinkProps['to'];
  };

type Props = ButtonProps | AnchorProps | RouterLinkProps;

export function NeobrutalButton(props: Props) {
  const { variant = 'primary', size = 'md', className, children } = props;
  const classes = clsx(BASE, VARIANTS[variant], SIZES[size], className);

  if (props.as === 'a') {
    const {
      as,
      variant: _variant,
      size: _size,
      className: _className,
      children: _children,
      ...rest
    } = props;
    void as;
    void _variant;
    void _size;
    void _className;
    void _children;
    return (
      <a className={classes} {...rest}>
        {children}
      </a>
    );
  }

  if (props.as === 'link') {
    const {
      as,
      variant: _variant,
      size: _size,
      className: _className,
      children: _children,
      ...rest
    } = props;
    void as;
    void _variant;
    void _size;
    void _className;
    void _children;
    return (
      <Link className={classes} {...rest}>
        {children}
      </Link>
    );
  }

  const {
    as,
    variant: _variant,
    size: _size,
    className: _className,
    children: _children,
    ...rest
  } = props;
  void as;
  void _variant;
  void _size;
  void _className;
  void _children;
  return (
    <button className={classes} {...rest}>
      {children}
    </button>
  );
}
